import { useEffect, useMemo, useRef, useState } from "react";
import { roman } from "../shared/plates";
import { SameOpeningFor } from "./seeds";
import { PLAYER_VOICES, VOICE_BLURBS, VOICE_NAMES } from "../shared/voices";
import { chapterAt, chaptersOf, findIn, wordScore, wordsOf, type ChapterInfo, type NameHit, type NameKind } from "../shared/find";
import type { StoryView } from "../shared/story";
import { api } from "./api";
import { PAD, clothRows, drawCloth, rowSpan, skinNow } from "./cloth";
import { OUTCOME_LABEL, scrollParent } from "./common";
import { goToTurn, topTurn, turnEls } from "./nav";
import { NoirMap, withArticle } from "./noirmap";
import { mapOf } from "../shared/map";

/**
 * Finding your way in a long story. The story rail: the cloth as a narrow strip down the left of the page, one row
 * per turn, with the chapters marked and a window over the turns on screen. Pointing at it opens the loupe beside it:
 * the same cloth at full detail, about twenty turns around the pointer, where a click lands on exactly the turn you
 * mean (the wheel moves it a turn at a time); dragging the strip itself scrolls the story. On a
 * phone it is a thin strip at the right edge that opens the journal. The journal (once "the story map"): every chapter (its question,
 * how it ended, who came in), every name, and a search that forgives a half-remembered word. All from the page's own
 * StoryView (shared/find.ts): nothing is fetched and no model is asked.
 */

/** Shorter stories don't need a map. */
export const RAIL_MIN_TURNS = 6;
const RAIL_W = 24;
const PHONE_W = 8;
const WIDE = "(min-width: 1024px)";

function useWide() {
  const [wide, setWide] = useState(() => typeof matchMedia === "function" && matchMedia(WIDE).matches);
  useEffect(() => {
    const m = matchMedia(WIDE);
    const on = () => setWide(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return wide;
}

/** The turns on screen now: the first one whose start is above the top of the reading area, through the last one begun. */
function useOnScreen(dep: unknown): { a: number; b: number } | null {
  const [range, setRange] = useState<{ a: number; b: number } | null>(null);
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      const els = turnEls();
      if (!els.length) return setRange(null);
      const box = scrollParent(els[0]!)?.getBoundingClientRect();
      const top = (box?.top ?? 0) + 8, bottom = box?.bottom ?? window.innerHeight;
      let a = Number(els[0]!.dataset.turn), b = a;
      for (const el of els) {
        const y = el.getBoundingClientRect().top, n = Number(el.dataset.turn);
        if (y <= top) a = n;
        if (y < bottom) b = n;
      }
      setRange((r) => (r && r.a === a && r.b === Math.max(a, b) ? r : { a, b: Math.max(a, b) }));
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(measure); };
    schedule();
    document.addEventListener("scroll", schedule, { capture: true, passive: true });
    window.addEventListener("resize", schedule);
    return () => { document.removeEventListener("scroll", schedule, { capture: true }); window.removeEventListener("resize", schedule); cancelAnimationFrame(raf); };
  }, [dep]);
  return range;
}

const chapterName = (c: ChapterInfo) => (c.k ? `Chapter ${roman(c.k)}` : "Before the first chapter");
const turnsLabel = (c: ChapterInfo) => (c.from === c.to ? `turn ${c.from}` : `turns ${c.from}–${c.to}`);

// ---- the loupe: the rail up close, as rows you can read --------------------------------------------------------------

const LOUPE_W = 340;
const CLOTH_W = 44;
const ROW_H = 30;
const LOUPE_ROWS = 11;
const LOUPE_PICKS = 5;
const CLOSE_MS = 280;

interface LoupeAt { start: number; focus: number; top: number }

/** Tension as five pips (the director keeps it 1..5). */
const pips = (t: number | undefined) => Math.max(1, Math.min(5, Math.round(t ?? 3)));

function Loupe({ view, rows, chapters, at, onFocus, onShift, onGo, onEnter, onLeave }: {
  view: StoryView; rows: number[]; chapters: ChapterInfo[]; at: LoupeAt;
  onFocus(i: number): void; onShift(d: number): void; onGo(n: number): void; onEnter(): void; onLeave(): void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const shown = Math.min(LOUPE_ROWS, rows.length);
  const LH = shown * ROW_H;
  const skin = skinNow();
  const byN = useMemo(() => new Map(view.turns.map((t) => [t.n, t])), [view]);
  // The cloth's own rows for the window, at loupe scale (redrawn only when the window moves).
  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = Math.round(CLOTH_W * dpr); c.height = Math.round(LH * dpr);
    const g = c.getContext("2d")!;
    const pad = Math.round(CLOTH_W * PAD);
    g.setTransform(dpr, 0, 0, dpr, 0, -(pad + at.start * ROW_H) * dpr);
    drawCloth(g, view, CLOTH_W, rows.length * ROW_H + pad * 2, skin, { picks: LOUPE_PICKS });
  }, [view, rows.length, at.start, LH, skin]);
  // The wheel moves the window a turn at a time (a native listener: React's wheel events can't prevent scrolling).
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => { e.preventDefault(); if (Math.abs(e.deltaY) > 1) onShift(e.deltaY > 0 ? 1 : -1); };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, [onShift]);

  const n = rows[at.focus]!;
  const ch = chapterAt(chapters, n);
  const starts = new Set(chapters.filter((c) => c.k > 0).map((c) => rows.find((r) => r >= c.from)));
  return (
    <div ref={box} className="lm-loupe" style={{ top: at.top }} onPointerEnter={onEnter} onPointerLeave={onLeave} aria-hidden="true">
      <div className="lm-loupe-rows" style={{ height: LH }}
        onPointerMove={(e) => onFocus(at.start + Math.floor((e.clientY - e.currentTarget.getBoundingClientRect().top) / ROW_H))}
        onClick={() => onGo(n)}>
        <canvas ref={canvas} style={{ width: CLOTH_W, height: LH }} />
        {rows.slice(at.start, at.start + shown).map((r, k) => {
          const t = byN.get(r);
          const c = starts.has(r) ? chapterAt(chapters, r) : null;
          return (
            <div key={r} className={`lm-loupe-row ${at.start + k === at.focus ? "lm-loupe-on" : ""} ${c ? "lm-loupe-new" : ""}`} style={{ top: k * ROW_H, height: ROW_H }}>
              <span className="lm-loupe-n">{r}</span>
              <span className="lm-loupe-pips" title={`tension ${pips(t?.tension)} of 5`}>{"●".repeat(pips(t?.tension))}<i>{"●".repeat(5 - pips(t?.tension))}</i></span>
              <span className="lm-loupe-line">
                {c && <b className="lm-loupe-ch">{chapterName(c).replace("Chapter ", "")}</b>}
                {t?.spike && <b className="lm-loupe-mark" title="a sudden event">✦</b>}
                {t?.status === "cut" && <b className="lm-loupe-mark" title="you cut in">✂</b>}
                {t?.input ?? ""}
              </span>
            </div>
          );
        })}
      </div>
      <div className="lm-loupe-caption">
        <p><strong>Turn {n}</strong>{ch ? ` · ${chapterName(ch)}` : ""}</p>
        {ch?.question && <p className="lm-loupe-q">{ch.question}</p>}
        <p className="lm-loupe-hint">Click a row to go there · the wheel moves one turn · Esc closes</p>
      </div>
    </div>
  );
}

// ---- the rail --------------------------------------------------------------------------------------------------------

export function StoryRail({ view, onOpen }: { view: StoryView; onOpen(mode: "find" | "chapters" | "journal"): void }) {
  const wide = useWide();
  const ref = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [H, setH] = useState(0);
  const [loupe, setLoupe] = useState<LoupeAt | null>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const dragging = useRef(false);
  const shown = view.turns.length >= RAIL_MIN_TURNS;
  const W = wide ? RAIL_W : PHONE_W;
  const chapters = useMemo(() => chaptersOf(view), [view]);
  const on = useOnScreen(view.turns.length);

  // Room for the rail beside the page on wide screens.
  useEffect(() => {
    if (!shown || !wide) return;
    document.body.classList.add("lm-railed");
    return () => document.body.classList.remove("lm-railed");
  }, [shown, wide]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setH(el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [shown, wide]);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);
  useEffect(() => {
    if (!loupe) return;
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") setLoupe(null); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [!!loupe]);
  useEffect(() => {
    const c = canvas.current;
    if (!c || !H) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = Math.round(W * dpr); c.height = Math.round(H * dpr);
    const g = c.getContext("2d")!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawCloth(g, view, W, H);
  }, [view, W, H]);

  if (!shown) return null;
  const span = (n: number) => rowSpan(view, W, H, n);
  const win = on && H ? { top: span(on.a)!.y0, bottom: span(on.b)!.y1 } : null;
  const rows = clothRows(view);
  const R = rows.length;
  /** The row (index into `rows`) under a point of the strip. */
  const rowAt = (y: number) => {
    const pad = Math.round(W * PAD);
    const f = Math.min(0.9999, Math.max(0, (y - pad) / (H - pad * 2)));
    return Math.floor(f * R);
  };
  const yOf = (e: React.PointerEvent) => e.clientY - e.currentTarget.getBoundingClientRect().top;
  const keepOpen = () => window.clearTimeout(closeTimer.current);
  const closeSoon = () => { keepOpen(); closeTimer.current = window.setTimeout(() => setLoupe(null), CLOSE_MS); };
  /** Opens (or moves) the loupe on row i, level with the pointer. */
  const aim = (i: number, clientY: number) => {
    keepOpen();
    const shown = Math.min(LOUPE_ROWS, R);
    const start = Math.max(0, Math.min(R - shown, i - Math.floor(shown / 2)));
    const top = Math.max(8, Math.min(window.innerHeight - shown * ROW_H - 150, clientY - (i - start) * ROW_H - ROW_H / 2));
    setLoupe({ start, focus: i, top });
  };

  if (!wide) {
    return (
      <button type="button" className="lm-rail-phone" ref={ref as unknown as React.Ref<HTMLButtonElement>} onClick={() => onOpen("journal")}
        aria-label={`Journal: ${chapters.length} chapter${chapters.length === 1 ? "" : "s"}, ${view.turns.length} turns`}>
        <canvas ref={canvas} style={{ width: W, height: H || undefined }} aria-hidden="true" />
        {win && <span className="lm-rail-window" style={{ top: win.top, height: Math.max(4, win.bottom - win.top) }} aria-hidden="true" />}
      </button>
    );
  }

  return (
    <nav className="lm-rail" aria-label="Turns and chapters">
      <div className="lm-rail-tools">
        <button type="button" onClick={() => onOpen("find")} aria-label="Find in the story" title="Find in the story">
          <svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="8.5" cy="8.5" r="5.5" /><path d="M13 13l4.5 4.5" /></svg>
        </button>
        <button type="button" onClick={() => onOpen("chapters")} aria-label="Chapters" title="Chapters">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 5h12M4 10h12M4 15h8" /></svg>
        </button>
      </div>
      <div ref={ref} className="lm-rail-strip" role="slider" tabIndex={0} aria-label="Your place in the turns"
        aria-valuemin={view.turns[0]?.n ?? 1} aria-valuemax={view.turns.at(-1)?.n ?? 1} aria-valuenow={on?.a ?? 1}
        aria-valuetext={on ? `Turn ${on.a}${chapterAt(chapters, on.a) ? `, ${chapterName(chapterAt(chapters, on.a)!)}` : ""}` : undefined}
        onKeyDown={(e) => {
          const at = on?.a ?? 1;
          const ch = chapterAt(chapters, at);
          const i = ch ? chapters.indexOf(ch) : 0;
          let to: number | null = null;
          if (e.key === "ArrowDown") to = at + 1;
          else if (e.key === "ArrowUp") to = at - 1;
          else if (e.key === "PageDown") to = chapters[i + 1]?.from ?? null;
          else if (e.key === "PageUp") to = ch && at > ch.from ? ch.from : chapters[i - 1]?.from ?? null;
          else if (e.key === "Home") to = view.turns[0]?.n ?? null;
          else if (e.key === "End") to = view.turns.at(-1)?.n ?? null;
          if (to !== null) { e.preventDefault(); goToTurn(Math.max(1, to), { smooth: false }); }
        }}
        // Pointing and dragging only move the loupe; the story moves when you let go (or click a loupe row).
        onPointerDown={(e) => {
          if (!R) return;
          dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId);
          aim(rowAt(yOf(e)), e.clientY);
        }}
        onPointerMove={(e) => { if (R) aim(rowAt(yOf(e)), e.clientY); }}
        onPointerUp={(e) => {
          if (!dragging.current || !R) return;
          dragging.current = false;
          setLoupe(null);
          goToTurn(rows[rowAt(yOf(e))]!);
        }}
        onPointerCancel={() => { dragging.current = false; }}
        onPointerLeave={() => { if (!dragging.current) closeSoon(); }}>
        <canvas ref={canvas} style={{ width: W, height: H || undefined }} aria-hidden="true" />
        {H > 0 && chapters.filter((c) => c.k > 0).map((c) => (
          <span key={c.k} className={`lm-rail-mark ${c.outcome ? `lm-rail-${c.outcome}` : ""}`} style={{ top: span(c.from)!.y0 }} aria-hidden="true" />
        ))}
        {win && <span className="lm-rail-window" style={{ top: win.top, height: Math.max(4, win.bottom - win.top) }} aria-hidden="true" />}
      </div>
      {loupe && R > 0 && (
        <Loupe view={view} rows={rows} chapters={chapters} at={loupe}
          onFocus={(i) => setLoupe((l) => (l ? { ...l, focus: Math.max(l.start, Math.min(l.start + Math.min(LOUPE_ROWS, R) - 1, i)) } : l))}
          onShift={(d) => setLoupe((l) => {
            if (!l) return l;
            const shown = Math.min(LOUPE_ROWS, R);
            const start = Math.max(0, Math.min(R - shown, l.start + d));
            return { ...l, start, focus: Math.max(0, Math.min(R - 1, l.focus + d)) };
          })}
          onGo={(n) => { setLoupe(null); goToTurn(n); }}
          onEnter={keepOpen} onLeave={closeSoon} />
      )}
    </nav>
  );
}

// ---- the journal ---------------------------------------------------------------------------------------------------

const KIND_LABEL: Record<NameKind, string> = { person: "person", place: "place", thing: "thing", thread: "thread" };

/** The journal's tabs; Chapters only while the story has chapters. */
type Tab = "threads" | "places" | "kept" | "chapters";
const TABS: { id: Tab; label: string }[] = [
  { id: "threads", label: "Threads" }, { id: "places", label: "Places" }, { id: "kept", label: "Kept" }, { id: "chapters", label: "Chapters" },
];
const TAB_KEY = "loom:journal-tab";
const PLACES_KEY = "loom:places-as";
const savedTab = (): Tab => { try { const t = localStorage.getItem(TAB_KEY); return TABS.some((x) => x.id === t) ? (t as Tab) : "threads"; } catch { return "threads"; } };
const saveTab = (t: Tab) => { try { localStorage.setItem(TAB_KEY, t); } catch { /* the default next time */ } };

/**
 * The journal (once "the story map"): where you are and the chapter's question, find, then one tab at a time: the
 * loose ends you can bring back, the places you've been, what you've kept, the chapters. The story's settings (its
 * narrator, chapters on or off) sit behind ⚙. Opened from "Chapter N" it shows the chapters; from find, the search;
 * otherwise the tab last used.
 */
export function StoryMap({ view, mode, onClose, onChanged, onPull, onFill, onLook }: {
  view: StoryView; mode: "find" | "chapters" | "journal" | "places"; onClose(): void; onChanged?(): void; onPull?(t: StoryView["threads"][number]): void;
  /** Puts a line in the writer ("go there" fills "/elsewhere the harbour"). */
  onFill?(text: string): void;
  /** Looks closer at a name (from the map: the journal closes and the look opens at the latest turn). */
  onLook?(name: string): void;
}) {
  const mapData = useMemo(() => mapOf(view), [view]);
  const hereArea = mapData.places.find((p) => p.id === mapData.here)?.area;
  const [placesAs, setPlacesAs] = useState<"map" | "list">(() => {
    try { const v = localStorage.getItem(PLACES_KEY); if (v === "map" || v === "list") return v; } catch { /* the default */ }
    return mapData.places.length >= 2 ? "map" : "list";
  });
  const showPlacesAs = (v: "map" | "list") => { setPlacesAs(v); try { localStorage.setItem(PLACES_KEY, v); } catch { /* this visit only */ } };
  const [striking, setStriking] = useState<string | null>(null);
  const strike = (kind: "truth" | "house", text: string) => {
    setStriking(text);
    api.strike(view.id, kind, text).then(() => onChanged?.(), () => {}).finally(() => setStriking(null));
  };
  // Places the player has stood in (the canon pass records where they are each turn), never ones only mentioned. A
  // story from before that has no record: every known place, except one the open chapter is asking them to reach.
  const allPlaces = view.things.filter((t) => t.kind === "place");
  const places = view.been.length
    ? view.been.flatMap((id) => allPlaces.filter((p) => p.id === id))
    : allPlaces.filter((p) => !view.chapter || !view.chapter.question.toLowerCase().includes(p.name.toLowerCase().replace(/^(the|a|an) /, "")));
  const hereName = view.here ? allPlaces.find((p) => p.id === view.here)?.name : undefined;
  const [flowBusy, setFlowBusy] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const setVoice = (voice: string) => {
    setVoiceBusy(true);
    api.voice(view.id, voice).then(() => onChanged?.(), () => {}).finally(() => setVoiceBusy(false));
  };
  const voiceIds: string[] = view.voice === "classic" ? [...PLAYER_VOICES, "classic"] : [...PLAYER_VOICES];
  const setFree = (free: boolean) => {
    setFlowBusy(true);
    api.flow(view.id, free).then(() => onChanged?.(), () => {}).finally(() => setFlowBusy(false));
  };
  const tabs = TABS.filter((t) => t.id !== "chapters" || !view.free);
  const [tab, setTabState] = useState<Tab>(() => {
    const t = mode === "chapters" ? "chapters" : mode === "places" ? "places" : savedTab();
    return tabs.some((x) => x.id === t) ? t : "threads";
  });
  const setTab = (t: Tab) => { setTabState(t); saveTab(t); };
  useEffect(() => { if (view.free && tab === "chapters") setTabState("threads"); }, [view.free, tab]);
  const [settings, setSettings] = useState(false);
  const [q, setQ] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const chapters = useMemo(() => chaptersOf(view), [view]);
  const result = useMemo(() => (q.trim() ? findIn(view, q) : null), [view, q]);
  // The chapter on screen when the journal opened (marked in Chapters).
  const reading = useMemo(() => {
    const els = turnEls();
    let a = els[0] ? Number(els[0].dataset.turn) : 1;
    for (const el of els) if (el.getBoundingClientRect().top <= 80) a = Number(el.dataset.turn);
    return a;
  }, []);
  // Loose ends: the quietest first (they're the ones worth bringing back); ones in play now can't be brought back.
  const threads = view.threads.filter((t) => t.state === "open")
    .map((t) => ({ t, quiet: view.n - Math.max(t.turn, ...t.seen) }))
    .sort((a, b) => b.quiet - a.quiet);

  useEffect(() => { if (mode === "find") input.current?.focus(); }, [mode]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { if (settings) setSettings(false); else onClose(); } };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose, settings]);

  const go = (n: number, terms?: string[]) => { onClose(); requestAnimationFrame(() => goToTurn(n, { terms })); };
  const chapterOf = (n: number) => chapterAt(chapters, n);
  const canAct = !view.ended;

  return (
    <div className="lm-map-back" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="lm-map" role="dialog" aria-modal="true" aria-labelledby="lm-map-h">
        <header className="lm-map-head">
          <h2 id="lm-map-h"><span className="lm-map-eyebrow">{settings ? "This story's settings" : "Journal"}</span>{view.title || "Your story"}</h2>
          <button type="button" className="lm-map-icon" onClick={() => setSettings(!settings)} aria-pressed={settings}
            aria-label={settings ? "Back to the journal" : "This story's settings: narrator, chapters"} title={settings ? "Back to the journal" : "Settings: narrator, chapters"}>
            {settings ? "‹" : <svg viewBox="0 0 20 20" aria-hidden="true"><circle className="lm-cog-teeth" cx="10" cy="10" r="6.6" /><circle cx="10" cy="10" r="4.6" /><circle cx="10" cy="10" r="1.7" /></svg>}
          </button>
          <button type="button" className="lm-map-icon lm-map-close" onClick={onClose} aria-label="Close your journal">×</button>
        </header>

        {settings ? (
          <div className="lm-map-body">
            <section aria-label="Narrator">
              <h3>Narrator</h3>
              <ul className="lm-jr-voices" role="radiogroup" aria-label="Narrator for this story">
                {voiceIds.map((id) => (
                  <li key={id}>
                    <button type="button" role="radio" aria-checked={view.voice === id} disabled={voiceBusy || view.ended} onClick={() => view.voice !== id && setVoice(id)}>
                      <span className="lm-jr-dot" aria-hidden="true" />
                      <strong>{VOICE_NAMES[id as keyof typeof VOICE_NAMES] ?? id}</strong>
                      <span className="lm-map-meta">{VOICE_BLURBS[id as keyof typeof VOICE_BLURBS] ?? ""}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {view.voiceSince && (() => {
                const ch = chapterOf(view.voiceSince.n);
                const from = view.voiceSince.from;
                return (
                  <p className="lm-map-meta lm-jr-turned">
                    <strong>{VOICE_NAMES[view.voice as keyof typeof VOICE_NAMES] ?? view.voice}</strong> · since {ch && ch.k > 0 ? `chapter ${ch.k}` : `turn ${view.voiceSince.n}`}. The story changed, and so did its telling.{" "}
                    <button type="button" className="lm-linkish" disabled={voiceBusy || view.ended} onClick={() => setVoice(from)}>Go back to {VOICE_NAMES[from as keyof typeof VOICE_NAMES] ?? from}</button>
                  </p>
                );
              })()}
              <p className="lm-map-empty">A change takes effect from the next reply.</p>
            </section>
            <section aria-label="Chapters">
              <div className="lm-jr-switchrow">
                <h3 id="lm-jr-ch">Chapters</h3>
                <button type="button" className="lm-jr-switch" role="switch" aria-checked={!view.free} aria-labelledby="lm-jr-ch" disabled={flowBusy || view.ended}
                  onClick={() => setFree(!view.free)}><span aria-hidden="true" /></button>
                <span className="lm-map-meta">{view.free ? "Off" : "On"}</span>
              </div>
              <p className="lm-map-empty">{view.free
                ? "This story flows freely: no chapter questions, no chapter endings, nothing to resolve. Tension still rises and falls, and things still happen. Turn chapters on any time; asking for an ending brings back one last chapter."
                : "Each chapter asks a question, builds to a moment of decision, and ends: earned, at a price, or lost. Turn chapters off and the story simply flows."}</p>
            </section>
            <p className="lm-map-empty">Defaults for new stories are in Account settings.</p>
          </div>
        ) : (
          <>
            {(hereName || (view.chapter && !view.free)) && (
              <div className="lm-jr-now">
                {hereName && <p><span className="lm-map-meta">You're in</span> {hereName}</p>}
                {view.chapter && !view.free && (
                  <p><span className="lm-map-meta">Chapter {roman(view.chapter.n)}{view.chapter.crisis ? " · the moment of decision" : ""}</span> <span className="lm-jr-q">{view.chapter.question}</span></p>
                )}
              </div>
            )}
            <form className="lm-map-find" role="search" onSubmit={(e) => { e.preventDefault(); const t = result?.turns[0]; if (t) go(t.n, result!.terms); }}>
              <label htmlFor="lm-map-q" className="sr">Find in the story</label>
              <input id="lm-map-q" ref={input} type="search" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" spellCheck={false}
                placeholder="Find a name, a place, a line" />
            </form>
            {!result && (
              <div className="lm-jr-tabs" role="tablist" aria-label="Journal">
                {tabs.map((t) => (
                  <button key={t.id} type="button" role="tab" id={`lm-jr-tab-${t.id}`} aria-selected={tab === t.id} aria-controls="lm-jr-panel"
                    onClick={() => setTab(t.id)}>{t.label}</button>
                ))}
              </div>
            )}

            <div className="lm-map-body" {...(result ? {} : { id: "lm-jr-panel", role: "tabpanel", "aria-labelledby": `lm-jr-tab-${tab}` })}>
              {result ? (
                <>
                  {!result.names.length && !result.turns.length && (
                    <p className="lm-map-empty">Nothing in the story matches “{q.trim()}”. Try fewer words, or spell it the way it sounded.</p>
                  )}
                  {result.names.length > 0 && (
                    <section aria-label="Names">
                      <h3>Names</h3>
                      <ul className="lm-map-names">
                        {result.names.slice(0, 8).map((x) => (
                          <li key={x.id}>
                            <button type="button" className="lm-map-name" onClick={() => setQ(x.name)}>
                              <strong>{x.name}</strong> <span className="lm-map-kind">{KIND_LABEL[x.kind]}</span>
                              {!x.byName && <span className="lm-map-why">{x.hint}</span>}
                            </button>
                            <button type="button" className="lm-map-jump" onClick={() => go(x.seen[0]!, wordsOf(x.name).map((w) => w.w))}>
                              first in turn {x.seen[0]}{x.seen.length > 1 ? ` · in ${x.seen.length} turns` : ""}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                  {result.turns.length > 0 && (
                    <section aria-label="Turns">
                      <h3>{result.turns.length + result.more} turn{result.turns.length + result.more === 1 ? "" : "s"}</h3>
                      {!result.turns.some((t) => t.exact) && <p className="lm-map-empty">No exact match for “{q.trim()}”. These are the closest.</p>}
                      <ol className="lm-map-hits">
                        {result.turns.map((t) => {
                          const ch = chapterOf(t.n);
                          return (
                            <li key={t.n}>
                              <button type="button" onClick={() => go(t.n, result.terms)}>
                                <span className="lm-map-meta">Turn {t.n}{ch ? ` · ${chapterName(ch)}` : ""}{t.where === "you" ? " · your line" : ""}{!t.exact && result.turns.some((x) => x.exact) ? " · close match" : ""}</span>
                                <span className="lm-map-snip">{t.snippet.map((p, i) => (p.hit ? <mark key={i}>{p.text}</mark> : <span key={i}>{p.text}</span>))}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ol>
                      {result.more > 0 && <p className="lm-map-empty">and {result.more} more. Add a word to narrow it down.</p>}
                    </section>
                  )}
                </>
              ) : tab === "threads" ? (
                <section aria-label="Threads">
                  <p className="lm-map-empty">{threads.length
                    ? "Loose ends the story hasn't settled. Tap one to bring it back on your next line; that line uses a turn."
                    : "No loose ends right now. When the story leaves something open (a debt, a stranger's name), it's listed here."}</p>
                  <ul className="lm-jr-rows">
                    {threads.map(({ t, quiet }) => (
                      <li key={t.id}>
                        {quiet > 1 && onPull && canAct ? (
                          <button type="button" className="lm-jr-row" onClick={() => { onClose(); onPull(t); }} title={t.hint}>
                            <span>↺ {t.name}</span><span className="lm-map-meta">quiet for {quiet} turns</span>
                          </button>
                        ) : (
                          <div className="lm-jr-row lm-jr-still" title={t.hint}><span>{t.name}</span><span className="lm-map-meta">{quiet > 1 ? `quiet for ${quiet} turns` : "in play now"}</span></div>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : tab === "places" ? (
                <section aria-label="Places you've been">
                  {mapData.places.length > 0 && (
                    <div className="lm-jr-seg" role="group" aria-label="Show places as">
                      <button type="button" aria-pressed={placesAs === "map"} onClick={() => showPlacesAs("map")}>Map</button>
                      <button type="button" aria-pressed={placesAs === "list"} onClick={() => showPlacesAs("list")}>List</button>
                    </div>
                  )}
                  {placesAs === "map" && mapData.places.length > 0 ? (
                    <NoirMap view={view} onLook={onLook} onFill={onFill} onPull={onPull} onClose={onClose} />
                  ) : (<>
                  <p className="lm-map-empty">{places.length
                    ? (onFill && canAct ? "Where you've been. “Go there” puts /elsewhere in your line: going back uses a turn, and time passes while you're away." : "Where you've been.")
                    : "No places yet."}</p>
                  <ul className="lm-jr-rows">
                    {places.map((p) => {
                      const inside = view.things.filter((t) => t.parent === p.id && t.kind !== "place");
                      return (
                        <li key={p.id}>
                          <div className="lm-jr-row lm-jr-place">
                            <span title={p.hint}>{p.name}</span>
                            {p.id === view.here ? <span className="lm-map-youhere">you're here</span>
                              : p.gone ? <span className="lm-map-youhere">gone</span>
                              : onFill && canAct ? <button type="button" className="lm-map-jump" onClick={() => {
                                onClose();
                                // Within the area you're in it's a walk; anywhere else a /elsewhere jump.
                                const near = !!hereArea && mapData.places.find((m) => m.id === p.id)?.area === hereArea;
                                onFill(near ? `I go to ${withArticle(p.name)}.` : `/elsewhere ${p.name}`);
                              }}>go there</button> : null}
                          </div>
                          {inside.length > 0 && (
                            <p className="lm-jr-inside">
                              {inside.slice(0, 6).map((t, k) => (
                                <span key={t.id}>{k ? " · " : ""}<button type="button" onClick={() => setQ(t.name)} title={`Find “${t.name}” in the story`}>{t.name}</button></span>
                              ))}
                              {inside.length > 6 ? ` · +${inside.length - 6}` : ""}
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  </>)}
                </section>
              ) : tab === "kept" ? (
                <section aria-label="Kept">
                  <p className="lm-map-empty">{view.truths.length || view.house.length
                    ? "Facts your own lines made true, and what you asked of the storyteller. Strike one and it stops binding the story from now on; what was already written stays."
                    : "Nothing kept yet. When the story lets a fact from your line stand (“I carry my father's watch”), it's listed here, and so is anything you ask of the storyteller (“slower, please”)."}</p>
                  <ul className="lm-jr-rows">
                    {view.truths.map((t) => (
                      <li key={`t${t}`}><div className="lm-jr-row"><span>{t}</span>
                        <button type="button" className="lm-map-jump" disabled={striking === t} onClick={() => strike("truth", t)}>strike</button></div></li>
                    ))}
                    {view.house.map((h) => (
                      <li key={`h${h}`}><div className="lm-jr-row"><span className="lm-house">You asked: “{h}”</span>
                        <button type="button" className="lm-map-jump" disabled={striking === h} onClick={() => strike("house", h)}>strike</button></div></li>
                    ))}
                  </ul>
                </section>
              ) : (
                <section aria-label="Chapters">
                  <ol className="lm-map-chapters">
                    {chapters.map((c) => (
                      <li key={c.k} className={reading >= c.from && reading <= c.to ? "lm-map-here" : ""}>
                        <button type="button" onClick={() => go(c.from)}>
                          <span className="lm-map-meta">{c.k ? `Chapter ${roman(c.k)}` : "Opening"} · {turnsLabel(c)}{c.current && !view.ended ? " · now" : ""}</span>
                          {c.question && <span className="lm-map-q">{c.question}</span>}
                          {(c.outcome || (c.current && view.ended)) && (
                            <span className="lm-map-meta">{c.outcome ? OUTCOME_LABEL[c.outcome] : "The end"}{c.price ? ` · ${c.price}` : ""}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ol>
                  {view.seed ? <SameOpeningFor id={view.id} /> : null}
                  <p className="lm-chapter-pass"><button type="button" className="lm-linkish" onClick={() => window.dispatchEvent(new Event("weftwake:passon"))}>
                    {view.seed?.given ? "Your opening's link" : "Pass this opening on"}</button></p>
                </section>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

// ---- the ends: back to the beginning, on to the latest turn ---------------------------------------------------------

/** Two small buttons at the right: to the story's first turn, and back to the latest. Each shows only when it helps. */
export function EndsNav({ view }: { view: StoryView }) {
  const on = useOnScreen(view.turns.length);
  const first = view.turns[0]?.n, last = view.turns.at(-1)?.n;
  if (!on || first === undefined || last === undefined || view.turns.length < RAIL_MIN_TURNS) return null;
  const up = on.a > first + 1, down = on.b < last;
  if (!up && !down) return null;
  // Sit just above the writing line wherever it is, so the buttons never cover send, the footer or a status bar.
  const writer = document.getElementById("lm-input")?.closest("form")?.getBoundingClientRect();
  const bottom = writer && writer.top < window.innerHeight ? Math.max(16, window.innerHeight - writer.top + 12) : undefined;
  // Over the story's own column (the writing line's right edge), never over a side panel's map or cards.
  const right = writer ? Math.max(12, window.innerWidth - writer.right) : undefined;
  return (
    <nav className="lm-ends" aria-label="Beginning and latest turn" style={{ ...(bottom !== undefined ? { bottom } : {}), ...(right !== undefined ? { right } : {}) }}>
      {up && (
        <button type="button" onClick={() => goToTurn(first)} aria-label="Go to the beginning of the story" title="The beginning">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5 4h10M10 16V8M6 11.5L10 7.5l4 4" /></svg>
        </button>
      )}
      {down && (
        <button type="button" onClick={() => goToTurn(last)} aria-label="Go to the latest turn" title="The latest turn">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5 16h10M10 4v8M6 8.5l4 4 4-4" /></svg>
        </button>
      )}
    </nav>
  );
}

// ---- back to where you were ----------------------------------------------------------------------------------------

/** After any jump (rail, map, cloth, beginning/latest), a chip to go back to the turn you were reading, for a while. */
export function BackChip() {
  const [from, setFrom] = useState<number | null>(null);
  useEffect(() => {
    let t: number | undefined;
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ from: number }>).detail;
      setFrom(d.from);
      window.clearTimeout(t);
      t = window.setTimeout(() => setFrom(null), 20000);
    };
    window.addEventListener("weftwake:jumped", on);
    return () => { window.removeEventListener("weftwake:jumped", on); window.clearTimeout(t); };
  }, []);
  if (from === null) return null;
  return (
    <button type="button" className="lm-back" onClick={() => { goToTurn(from, { mark: false }); setFrom(null); }}>
      ↩ Back to turn {from}
    </button>
  );
}
